import { supabase } from "@/lib/supabase";

const MAX_RESUME_BYTES = 5 * 1024 * 1024;

export function parseIndianNumber(value) {
  if (value == null || value === "") return null;
  const normalized = String(value).replace(/[,₹\s]/g, "");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

function friendlyRpcError(message = "") {
  if (message.includes("already in this talent pool")) {
    return "You're already in this talent pool — we have your profile. To update your details, email careers@devbroz.com.";
  }
  if (message.includes("already applied to this role")) {
    return "You have already applied to this role. If you need to update your details, email careers@devbroz.com.";
  }
  if (message.includes("Consent is required")) {
    return "Please tick the consent box before submitting.";
  }
  if (message.includes("already registered with a different email")) {
    return "This phone number is already registered with a different email for this role.";
  }
  if (message.includes("Phone number is too short")) {
    return "Please enter a valid phone number with country code.";
  }
  if (message.includes("not found or not active")) {
    return "That role is no longer open. Please browse our current openings and try again.";
  }
  return message || "Something went wrong. Please try again or email careers@devbroz.com.";
}

export async function submitCareersApplication(form, roleSlug) {
  if (!supabase) {
    throw new Error("Application service is not configured. Please try again later.");
  }

  if (!form.resumeFile) {
    throw new Error("Please upload your resume (PDF).");
  }

  if (form.resumeFile.type !== "application/pdf") {
    throw new Error("Resume must be a PDF file.");
  }

  if (form.resumeFile.size > MAX_RESUME_BYTES) {
    throw new Error("Resume must be 5 MB or smaller.");
  }

  // Random suffix guards against two submissions landing in the same millisecond
  // (upsert is false, so a collision would reject the second applicant's upload).
  //
  // The path is decided HERE rather than by the upload, which is what lets the RPC run first:
  // submit_application is told where the file will live, and the bytes follow only if it
  // accepted the application.
  const uniqueSuffix = Math.random().toString(36).slice(2, 10);
  const resumePath = `${roleSlug}_${Date.now()}_${uniqueSuffix}.pdf`;

  // ---------------------------------------------------------------------------
  // Record the application FIRST, upload the résumé second.
  //
  // It used to be the other way round, with a `storage.remove()` on RPC failure to tidy up.
  // That cleanup could never work: the `resumes` bucket grants `anon` INSERT and nothing
  // else — there is no DELETE policy for anonymous callers — so the remove was refused every
  // time, and its `.catch(() => {})` swallowed the refusal. Every rejected submission
  // (duplicate application, missing consent, bad phone) therefore left the candidate's CV
  // sitting in storage with no application row pointing at it, and nothing to ever clean it
  // up. Three such files had accumulated by 2026-09-08, one of them a real candidate's.
  //
  // Doing the RPC first removes the need for a delete the browser is not allowed to perform.
  // The remaining failure mode — RPC accepted, upload then failed — leaves a row whose
  // resume_path has no object behind it, which is exactly what the worker's RESUME_MISSING
  // rule looks for. That is a visible, attributable, fixable state; an orphaned file is none
  // of those things.
  // ---------------------------------------------------------------------------
  const { data, error } = await supabase.rpc("submit_application", {
    p_full_name: form.fullName.trim(),
    p_email: form.email.trim().toLowerCase(),
    p_phone: form.phone.trim(),
    p_role_slug: roleSlug,
    p_total_exp_yrs: Number(form.totalExperienceYrs),
    p_skills: form.skills.trim(),
    p_relevant_experience: form.pitch.trim(),
    p_linkedin_url: form.linkedinUrl.trim(),
    p_city: form.city.trim(),
    p_current_employer: form.currentEmployer.trim(),
    p_current_ctc: parseIndianNumber(form.currentCtc),
    p_expected_ctc: parseIndianNumber(form.expectedCtc),
    p_notice_period: form.noticePeriod,
    p_english_rating: form.englishRating,
    p_resume_path: resumePath,
    p_source: form.source,
    // The checkbox used to be validated client-side and then thrown away — the function
    // hardcoded consent_given = true, so the stored value was not evidence of anything.
    p_consent: form.consent === true,
  });

  // Nothing has been uploaded at this point, so a rejection leaves no trace to clean up.
  if (error) {
    throw new Error(friendlyRpcError(error.message));
  }

  const { error: uploadError } = await supabase.storage
    .from("resumes")
    .upload(resumePath, form.resumeFile, {
      contentType: "application/pdf",
      upsert: false,
    });

  // The application is already recorded, so this must NOT be reported as a failed submission —
  // the candidate would try again and be told they have already applied. Hand the caller the
  // reference plus a flag, and let it show the success screen with a warning.
  if (uploadError) {
    return { ...data, resumeUploadFailed: true };
  }

  return data;
}
